import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Checkbox, DatePicker, Modal, Radio, Switch } from 'antd'
import dayjs from 'dayjs'
import {
  BarChartOutlined,
  DollarOutlined,
  FileTextOutlined,
  FolderOutlined,
} from '@ant-design/icons'
import type { AdvisoryAccountMapping, AdvisoryAiReportType } from '@/features/advisory-ai/api'
import {
  useAdvisoryAiReport,
  useAnalyzeAdvisoryReportMutation,
  useUpdateAdvisoryCallMutation,
} from '@/features/advisory-ai/hooks'
import { AdvisoryCallStatus } from '@/features/advisory-ai/types'
import { useToast } from '@/contexts/ToastContext'
import { isAdvisoryAccessDenied } from '@/features/advisory-ai/access'

type ReportConfig = {
  type: AdvisoryAiReportType
  label: string
  icon: React.ReactNode
}

const REPORT_CONFIGS: ReportConfig[] = [
  { type: 'profitability', label: 'Profit & Loss', icon: <BarChartOutlined className="text-2xl" /> },
  { type: 'cashflow', label: 'Cash Flow Statement', icon: <DollarOutlined className="text-2xl" /> },
  { type: 'accounts_receivable', label: 'Accounts Receivable', icon: <FileTextOutlined className="text-2xl" /> },
  { type: 'balance_sheet', label: 'Balance Sheet', icon: <FolderOutlined className="text-2xl" /> },
  { type: 'accounts_payable', label: 'Accounts Payable', icon: <FileTextOutlined className="text-2xl" /> },
]

type DataStepProps = {
  orgId: string
  callId: string
  clientId: string
  orgName: string
  onBack: () => void
  onContinue: () => void
}

const VALID_REPORT_TYPES: AdvisoryAiReportType[] = [
  'profitability',
  'cashflow',
  'accounts_receivable',
  'balance_sheet',
  'accounts_payable',
]

export function DataStep({ callId, clientId, onBack, onContinue }: DataStepProps) {
  const { addToast } = useToast()
  const [selectedReportTypes, setSelectedReportTypes] = useState<AdvisoryAiReportType[]>([])
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null])
  const [accountMapping, setAccountMapping] = useState<AdvisoryAccountMapping>('Reporting Code')
  const [includeHeadlinerSummary, setIncludeHeadlinerSummary] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const initializedFromReport = useRef(false)

  const selectedReportLabels = useMemo(
    () =>
      selectedReportTypes
        .map((type) => REPORT_CONFIGS.find((c) => c.type === type)?.label)
        .filter((label): label is string => Boolean(label)),
    [selectedReportTypes]
  )

  const reportQuery = useAdvisoryAiReport(callId, clientId)

  const meetingDateDisplay = useMemo(() => {
    const md = reportQuery.data?.meetingDate
    if (!md) return ''
    const d = dayjs(md)
    return d.isValid() ? d.format('DD/MM/YYYY h:mm A') : ''
  }, [reportQuery.data?.meetingDate])

  const internalMemoryDisplay = (reportQuery.data?.internalMemory ?? '').trim()

  const visibleAgendaItems = useMemo(
    () =>
      (reportQuery.data?.agendaItems ?? []).filter(
        (item) => item.title.trim().toLowerCase() !== 'executive summary'
      ),
    [reportQuery.data?.agendaItems]
  )
  const analyzeMutation = useAnalyzeAdvisoryReportMutation()
  const updateCallMutation = useUpdateAdvisoryCallMutation(callId, clientId)
  const status = String(reportQuery.data?.status ?? '').toUpperCase()
  const isLocked = status === AdvisoryCallStatus.COMPLETED_BRIEF || status === AdvisoryCallStatus.COMPLETED_EXPORT
  const shouldTriggerAnalysis = status === AdvisoryCallStatus.PENDING

  useEffect(() => {
    if (initializedFromReport.current || !reportQuery.data) return
    initializedFromReport.current = true
    const reports = reportQuery.data.reports ?? []
    const valid = reports.filter((r): r is AdvisoryAiReportType =>
      VALID_REPORT_TYPES.includes(r as AdvisoryAiReportType)
    ) as AdvisoryAiReportType[]
    if (valid.length > 0) setSelectedReportTypes(valid)
    const start = reportQuery.data.startDate
    const end = reportQuery.data.endDate
    if (start && end && dayjs(start).isValid() && dayjs(end).isValid()) {
      setDateRange([dayjs(start), dayjs(end)])
    }
    if (reportQuery.data.accountMapping) setAccountMapping(reportQuery.data.accountMapping)
    setIncludeHeadlinerSummary(Boolean(reportQuery.data.includeHeadlinerSummary))
  }, [reportQuery.data])

  useEffect(() => {
    initializedFromReport.current = false
  }, [callId])

  const isFormValid = selectedReportTypes.length > 0 && Boolean(dateRange[0] && dateRange[1])

  const handleToggleReport = (reportType: AdvisoryAiReportType) => {
    if (selectedReportTypes.includes(reportType)) {
      setSelectedReportTypes(selectedReportTypes.filter((t) => t !== reportType))
    } else {
      setSelectedReportTypes([...selectedReportTypes, reportType])
    }
  }

  const allReportsSelected = selectedReportTypes.length === REPORT_CONFIGS.length
  const someReportsSelected = selectedReportTypes.length > 0 && !allReportsSelected

  const handleToggleAllReports = (checked: boolean) => {
    if (isLocked) return
    setSelectedReportTypes(checked ? REPORT_CONFIGS.map((config) => config.type) : [])
  }

  const handleContinueClick = () => {
    if (isLocked) {
      onContinue()
      return
    }
    if (!clientId || !selectedReportTypes.length) return
    const start = dateRange[0]
    const end = dateRange[1]
    if (!start || !end || !start.isValid() || !end.isValid()) {
      addToast('Select a valid date range', 'error')
      return
    }
    if (!end.endOf('day').isAfter(start.startOf('day'))) {
      addToast('End date must be after start date', 'error')
      return
    }
    setConfirmOpen(true)
  }

  const handleConfirmSubmit = async () => {
    const start = dateRange[0]
    const end = dateRange[1]
    if (!start || !end) return
    try {
      await updateCallMutation.mutateAsync({
        // Send the picked calendar dates as plain YYYY-MM-DD (matching the create flow's
        // formatDateForApi). Using toISOString() on a local start-of-day rolled the day back in
        // +UTC zones (Oct 1 → 2025-09-30T16:00Z); date-only keeps the chosen day in any timezone.
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
        reports: selectedReportTypes,
        accountMapping,
        includeHeadlinerSummary,
      })
      if (shouldTriggerAnalysis) {
        await analyzeMutation.mutateAsync({
          reportId: callId,
          clientId,
          reportTypes: selectedReportTypes,
        })
        addToast('Analysis started', 'success')
      }
      setConfirmOpen(false)
      onContinue()
    } catch (error) {
      // A permission denial already carries the sentence that explains it; it is a warning,
      // not a failure.
      addToast(
        error instanceof Error ? error.message : 'Failed to start analysis',
        isAdvisoryAccessDenied(error) ? 'warning' : 'error'
      )
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Extract financial data</h2>
        <p className="text-sm text-gray-600">
          Select the reports you want to extract. Adjust the period or add/remove as needed.
        </p>
      </div>

      {isLocked && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Report type and date are set and cannot be changed.
        </p>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Period</label>
        <DatePicker.RangePicker
          value={dateRange}
          onChange={isLocked ? undefined : (values) => setDateRange(values as [dayjs.Dayjs | null, dayjs.Dayjs | null])}
          className="w-full max-w-md"
          format="DD/MM/YYYY"
          disabled={isLocked}
          disabledDate={(current) => Boolean(current && current.endOf('day').isAfter(dayjs().endOf('day')))}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Account mapping</label>
        <p className="text-xs text-gray-500 mb-2">Choose how extracted accounts are grouped in the analysis.</p>
        <Radio.Group
          value={accountMapping}
          onChange={(e) => setAccountMapping(e.target.value as AdvisoryAccountMapping)}
          disabled={isLocked}
          options={[
            { label: 'Reporting Code', value: 'Reporting Code' },
            { label: 'Account Type', value: 'Account Type' },
          ]}
          optionType="button"
          buttonStyle="solid"
        />
      </div>

      <div className="flex items-center gap-3">
        <Switch
          checked={includeHeadlinerSummary}
          onChange={setIncludeHeadlinerSummary}
          disabled={isLocked}
          aria-label="Include headliner summary"
        />
        <div>
          <span className="block text-sm font-medium text-gray-700">Include headliner summary</span>
          <span className="block text-xs text-gray-500">
            Add a short headline summary to the generated analysis.
          </span>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h3 className="text-base font-semibold text-gray-900">Reports to extract</h3>
          <Checkbox
            checked={allReportsSelected}
            indeterminate={someReportsSelected}
            disabled={isLocked}
            onChange={(e) => handleToggleAllReports(e.target.checked)}
          >
            Select all
          </Checkbox>
        </div>
        <div className="space-y-3">
          {REPORT_CONFIGS.map((config) => {
            const isSelected = selectedReportTypes.includes(config.type)
            return (
              <div
                key={config.type}
                className={`rounded-lg p-4 flex items-center gap-4 transition-all ${
                  isLocked
                    ? isSelected
                      ? 'feature-selection-card feature-selection-card--selected feature-selection-card--locked'
                      : 'border-2 border-gray-200 bg-gray-50 feature-selection-card--disabled'
                    : isSelected
                      ? 'feature-selection-card feature-selection-card--selected feature-selection-card--interactive'
                      : 'feature-selection-card feature-selection-card--interactive'
                }`}
                onClick={isLocked ? undefined : () => handleToggleReport(config.type)}
              >
                <div className={isSelected ? 'feature-soft-text' : 'text-gray-400'}>{config.icon}</div>
                <div className="flex-1">
                  <h4 className={`font-semibold ${isSelected ? 'feature-soft-text' : 'text-gray-600'}`}>
                    {config.label}
                  </h4>
                </div>
                {isSelected && (
                  <span className="feature-selection-pill shrink-0 rounded-full px-3 py-1 text-sm font-medium">
                    ✓ Selected
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button onClick={onBack}>← Back</Button>
        <Button
          type="primary"
          size="large"
          onClick={handleContinueClick}
          disabled={!isFormValid}
          loading={analyzeMutation.isPending || updateCallMutation.isPending}
          className="feature-button-solid"
        >
          {analyzeMutation.isPending || updateCallMutation.isPending ? 'Extracting...' : 'Continue →'}
        </Button>
      </div>

      <Modal
        title="Confirm extraction"
        open={confirmOpen}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmOpen(false)}
        okText="Confirm"
        cancelText="Cancel"
        confirmLoading={analyzeMutation.isPending || updateCallMutation.isPending}
        maskClosable={!(analyzeMutation.isPending || updateCallMutation.isPending)}
        closable={!(analyzeMutation.isPending || updateCallMutation.isPending)}
        width={520}
      >
        <p className="text-sm text-gray-600 mb-4">
          Please review the details below before continuing.
        </p>
        <dl className="space-y-3 text-sm max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <dt className="font-medium text-gray-700">Meeting time</dt>
            <dd className="text-gray-900 mt-0.5">{meetingDateDisplay || '—'}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-700">Additional notes</dt>
            <dd className="mt-0.5 whitespace-pre-wrap text-gray-900">
              {internalMemoryDisplay || '—'}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-700">Agenda items</dt>
            <dd className="mt-0.5">
              {visibleAgendaItems.length > 0 ? (
                <ul className="list-disc pl-5 space-y-1 text-gray-900">
                  {visibleAgendaItems.map((item) => (
                    <li key={item.advisoryAgendaId}>
                      <div className="font-medium">{item.title}</div>
                      {item.description?.trim() ? (
                        <div className="text-gray-600 text-xs mt-0.5 whitespace-pre-wrap">
                          {item.description}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-gray-900">—</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-700">Period</dt>
            <dd className="text-gray-900 mt-0.5">
              {dateRange[0] && dateRange[1]
                ? `${dateRange[0].format('DD/MM/YYYY')} – ${dateRange[1].format('DD/MM/YYYY')}`
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="font-medium text-gray-700">Account mapping</dt>
            <dd className="text-gray-900 mt-0.5">{accountMapping}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-700">Headliner summary</dt>
            <dd className="text-gray-900 mt-0.5">{includeHeadlinerSummary ? 'Included' : 'Not included'}</dd>
          </div>
          <div>
            <dt className="font-medium text-gray-700">Reports to extract</dt>
            <dd className="mt-0.5">
              {selectedReportLabels.length > 0 ? (
                <ul className="list-disc pl-5 space-y-0.5 text-gray-900">
                  {selectedReportLabels.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ul>
              ) : (
                <span className="text-gray-900">—</span>
              )}
            </dd>
          </div>
        </dl>
      </Modal>
    </div>
  )
}
